import React from "react";
import { Typography, Stack, CircularProgress } from "@mui/material";
import * as f from "../../../utils/formatter.js";
import { useLatest } from "../../../hooks.js";
import * as api from "../../../services/api.js";
import { isGrantedAny, role } from "shared/src/roles.mjs";
import { useRoles } from "../../../context/AuthenticationTokenContext";
import { insurance } from "shared/src/client.mjs";

function useEmployeeNameData(ids) {
  const [state, setState] = React.useState({
    loading: true,
    data: [],
  });

  useLatest(
    async (signal) => {
      try {
        setState((state) => ({ ...state, loading: true }));
        const { data } = await api.loadEmployeeNameData(ids);
        setState((state) => ({ ...state, data: data.employees.data }));
      } finally {
        if (!signal.aborted) {
          setState((state) => ({ ...state, loading: false }));
        }
      }
    },
    [ids],
  );

  return state;
}

function crerateArray(...items) {
  return items.filter((item) => item != null);
}

function LastNurse({ employeeAssignmentAudit }) {
  const ids = React.useMemo(
    () => crerateArray(employeeAssignmentAudit?.changed_fields?.employee_id),
    [employeeAssignmentAudit],
  );
  const employees = useEmployeeNameData(ids);

  if (employees.loading) {
    return <CircularProgress size={13} />;
  }

  return f.value(employees.data.map((e) => f.fullName(e)).join(", "));
}

function Info({ label, value }) {
  return (
    <Typography variant="body1">
      <strong>{label}</strong>
      <br />
      {value}
    </Typography>
  );
}

export function MoreInfoCard({
  client,
  lastAppointment,
  canSeeClientInsuranceCompany = false,
}) {
  const roles = useRoles();
  const canSeeClientCoordinator = isGrantedAny(
    roles,
    new Set([
      role.Floor_Manager,
      role.Treatment_Coordinator,
      role.Back_Office,
      role.Front_Office,
      role.Dental_Assistant,
      role.Manager_Clinic,
      role.Manager_Customer_Care,
      role.Read_Only,
      role.Read_Only_Full,
    ]),
  );
  const canSeeLastNurse = isGrantedAny(
    roles,
    new Set([
      role.Floor_Manager,
      role.Back_Office,
      role.Front_Office,
      role.Manager_Clinic,
      role.Manager_Customer_Care,
      role.Read_Only_Full,
    ]),
  );
  return (
    <Stack spacing={5} direction="row" sx={{ mb: 3 }}>
      <Stack spacing={5} direction="column" sx={{ mb: 3 }}>
        {canSeeClientInsuranceCompany && (
          <Info
            label="Pojišťovna"
            value={f.value(insurance[client.insurance_code])}
          />
        )}
        <Info
          label="Poslední app."
          value={
            lastAppointment == null
              ? f.value(null)
              : `${lastAppointment.service?.name}, ${f.date(lastAppointment.start)}`
          }
        />
      </Stack>
      <Stack spacing={5} direction="column" sx={{ mb: 3 }}>
        <>
          {canSeeClientCoordinator && (
            <Info
              label="Koordinátor"
              value={f.value(f.fullName(client?.coordinator))}
            />
          )}
          {canSeeLastNurse && (
            <Info
              label="Poslední ošetřující sestra"
              value={
                <LastNurse
                  employeeAssignmentAudit={
                    lastAppointment?.employeeAssignmentAudits?.data?.[0]
                  }
                />
              }
            />
          )}
        </>
      </Stack>
    </Stack>
  );
}
